import { filterCompanyDirectory } from "actions/companyDirectoryActions";
import { regionsActions } from "actions/regionsActions";
import { fetchTag } from "actions/tagsActions";
import { GET_VENDOR_TAXONOMY_URL } from "config/services";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import tagsService from "services/tagsService";
import axios from "utils/axios";
import { updateCompanyDirectory } from "utils/searchUtils";
import Slider from "../../../components/Slider";
import CreatedDateFilter from "./CreatedDateFilter";
import filterData from "./FiltersData";
import { CheckboxFilter } from "./FiltersType";
import { TaxonomyFilter } from "./tableComponent/components/FiltersType";

const isMixedCaseLabel = (value = "") =>
  /[a-z]/.test(value) && /[A-Z]/.test(value);

const getUniqueSortedValues = (items = []) => {
  const optionMap = new Map();

  items
    .filter((item) => item !== null && item !== undefined)
    .map((item) => String(item).trim())
    .filter(Boolean)
    .forEach((value) => {
      const key = value.toLowerCase();
      const existingValue = optionMap.get(key);

      if (
        !existingValue ||
        (!isMixedCaseLabel(existingValue) && isMixedCaseLabel(value))
      ) {
        optionMap.set(key, value);
      }
    });

  return [...optionMap.values()].sort((a, b) => a.localeCompare(b));
};

const buildFinancialYearOptions = (startYear = 2023) => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentFinancialYearStart =
    today.getMonth() >= 2 ? currentYear : currentYear - 1;

  return Array.from(
    { length: Math.max(currentFinancialYearStart - startYear + 1, 1) },
    (_, index) => {
      const year = startYear + index;
      return `${year}-${year + 1}`;
    },
  );
};

const formatFinancialYearLabel = (value = "") => {
  const [startYear, endYear] = String(value).split("-");
  return startYear && endYear ? `FY ${startYear}-${endYear.slice(-2)}` : value;
};

const amountRangeOptions = [
  { label: "Below Rs. 1 Lakh", min: 0, max: 100000 },
  { label: "Rs. 1L - Rs. 5L", min: 100000, max: 500000 },
  { label: "Rs. 5L - Rs. 25L", min: 500000, max: 2500000 },
  { label: "Rs. 25L - Rs. 50L", min: 2500000, max: 5000000 },
  { label: "Rs. 50L - Rs. 1 Cr", min: 5000000, max: 10000000 },
  { label: "Rs. 1 Cr - Rs. 5 Cr", min: 10000000, max: 50000000 },
  { label: "Above Rs. 5 Cr", min: 50000000, max: null },
];

const filterGroups = {
  tags: "Vendor Type",
  financialYear: "Order & Activity",
  vendorInvitationCount: "Order & Activity",
  vendorActivity: "Order & Activity",
  totalOrderValue: "Order & Activity",
  gstPanAvailability: "Compliance",
  location: "Location",
  addedBy: "Team & Date",
  createdDate: "Team & Date",
};

const parseAmountInput = (value = "") => {
  const amount = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(amount) ? amount : null;
};

const sanitizeAmountInput = (value = "") => String(value).replace(/\D/g, "");

const FilterGroupTitle = ({ title }) => (
  <div className="border-b border-[#E0E3E2] bg-[#F4F8F8] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-[#8A8A7A]">
    {title}
  </div>
);

const FilterHeader = ({ open, title, onClick }) => (
  <button
    type="button"
    className={`flex w-full items-center justify-between border-l-2 px-3 py-2 text-left transition-colors ${
      open
        ? "border-[#D65A22] bg-[#FFF7F2] text-[#8A3A1C]"
        : "border-transparent bg-white text-[#303026] hover:bg-[#FFF8F4]"
    }`}
    onClick={onClick}
  >
    <span className="text-xs font-semibold">{title}</span>
    <span
      className={`text-[#C8A99A] transition-transform ${
        open ? "rotate-180" : ""
      }`}
    >
      <svg
        aria-hidden="true"
        className="h-4 w-4"
        fill="currentColor"
        viewBox="0 0 20 20"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          clipRule="evenodd"
          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
          fillRule="evenodd"
        />
      </svg>
    </span>
  </button>
);

const FilterRadioRows = ({
  name,
  options,
  selectedValue,
  onChange,
  counts = {},
}) => (
  <div className="space-y-0.5 px-3 py-2">
    {options.map((option) => {
      const optionValue = typeof option === "string" ? option : option.value;
      const optionLabel = typeof option === "string" ? option : option.label;

      return (
        <label
          key={optionValue}
        className="flex cursor-pointer items-center justify-between rounded-md px-1 py-1 text-xs text-[#303026] hover:bg-[#FFF7F2]"
        >
          <span className="flex items-center gap-2">
            <input
              checked={selectedValue === optionValue}
              className="h-4 w-4 border-gray-300 text-[#D65A22] focus:ring-[#D65A22]"
              name={name}
              onChange={() => onChange(optionValue)}
              type="radio"
            />
            <span>{optionLabel}</span>
          </span>
          {counts[optionValue] !== undefined && (
            <span className="text-xs text-[#8A8A7A]">
              {counts[optionValue]}
            </span>
          )}
        </label>
      );
    })}
  </div>
);

const ToggleFilterRow = ({ label, active, onClick, icon, description }) => (
  <div className="border-b border-[#E8E8E0] px-3 py-2">
    <div className="flex items-center justify-between gap-3">
      <span className="flex min-w-0 flex-col text-xs font-semibold text-[#303026]">
        <span className="flex items-center gap-2">
          {label}
          {icon}
        </span>
        <span className="text-[10px] font-normal text-[#8A8A7A]">
          {description}
        </span>
      </span>
      <button
        type="button"
        onClick={onClick}
        className={`flex h-5 w-10 cursor-pointer items-center rounded-full p-1 transition-colors ${
          active ? "bg-[#D65A22]" : "bg-gray-300"
        }`}
      >
        <div
          className={`h-3 w-3 rounded-full bg-white shadow-md transition-transform ${
            active ? "translate-x-5" : "translate-x-0"
          }`}
        />
      </button>
    </div>
  </div>
);

const OnNeevayIcon = () => (
  <div
    className="flex items-center"
    style={{
      borderRadius: "3px",
      border: "0.5px solid #039400",
      background: "#E1FFDE",
      padding: "0 4px",
    }}
  >
    <span className="font-inter text-xs font-semibold text-black">N</span>
    <svg
      fill="none"
      height="5"
      viewBox="0 0 5 5"
      width="5"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="2.5" cy="2.5" fill="#039400" r="2.5" />
    </svg>
  </div>
);

const PromotedIcon = () => (
  <img
    alt="verified"
    className="h-[14px] w-[14px]"
    src="https://storagereponeevaydevcdn.blob.core.windows.net/business/Verified_image_nn.png"
  />
);

const LocationTrail = ({ state, city, area }) => (
  <span className="text-[10px] text-[#7A7A68]">
    {[state, city, area].filter(Boolean).join(" / ")}
  </span>
);

const AllFilters = ({ activeTab, containerRef }) => {
  const companyDirectory = useSelector((state) => state.companyDirectory);
  const vendorsPayload = useSelector((state) => state.companyDirectory);
  const regions = useSelector((state) => state.regions);
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const [openKeys, setOpenKeys] = useState({
    tags: false,
    addedBy: true,
    createdDate: true,
    totalOrderValue: true,
    financialYear: true,
    vendorInvitationCount: true,
    vendorActivity: true,
    gstPanAvailability: true,
    location: true,
  });
  const [searchTagTerm, setSearchTagTerm] = useState("");
  const [searchLocationTerm, setSearchLocationTerm] = useState("");
  const [tagsOptions, setTagsOptions] = useState([]);
  const [tags, setTags] = useState([]);
  const [readMoreTags, setReadMoreTags] = useState(true);
  const [taxonomyAll, setTaxonomyAll] = useState({});
  const [createdByOptions, setCreatedByOptions] = useState([]);
  const [allCreatedByOptions, setAllCreatedByOptions] = useState([]);
  const [customOrderValueMin, setCustomOrderValueMin] = useState("");
  const [customOrderValueMax, setCustomOrderValueMax] = useState("");
  const [customOrderValueError, setCustomOrderValueError] = useState("");

  const userProfile = auth.user;
  const userId = userProfile?.userId;
  const entityId = userProfile?.userEntity?.entityId;
  const regionDistricts = regions?.regionDistricts;
  const filtersFacets = filterData;
  const visibleFilterFacets = filtersFacets.filter(
    (facet) => !(activeTab === "MY_VENDORS" && facet.key === "addedBy"),
  );
  const isTagsFilterEnabled = filtersFacets.some((facet) => facet.key === "tags");
  const financialYearOptions = useMemo(() => buildFinancialYearOptions(), []);
  const appliedFilters = companyDirectory?.appliedFilters || {};
  const searchKey = companyDirectory?.searchKey;
  const selectedCity = appliedFilters?.location || "";
  const vendorActivityCounts = companyDirectory?.vendorActivityCounts || {};
  const vendorInvitationCounts = companyDirectory?.vendorInvitationCounts || {};
  const backendLocationOptions = companyDirectory?.locationFilterOptions || {};
  const backendCreatedByOptions = companyDirectory?.createdByFilterOptions;

  const fallbackLocationOptions = useMemo(() => {
    const cityAreaMap = new Map();
    const cities = [];
    const areas = [];

    (vendorsPayload?.vendors || []).forEach((vendor) => {
      (vendor?.location || []).forEach((location) => {
        const city = location?.city ? String(location.city).trim() : "";
        const area = location?.area ? String(location.area).trim() : "";
        const state = location?.state ? String(location.state).trim() : "";
        const cityKey = city.toLowerCase();

        if (city) {
          cities.push(city);
          if (!cityAreaMap.has(cityKey)) {
            cityAreaMap.set(cityKey, {
              city,
              state,
              areas: new Set(),
            });
          }
        }

        if (area) {
          areas.push(area);
          if (city) cityAreaMap.get(cityKey).areas.add(area);
        }
      });

      (vendor?.serviceLocations || []).forEach((location) => {
        if (typeof location === "string") areas.push(location);
      });
    });

    return {
      cities: getUniqueSortedValues(cities),
      areas: getUniqueSortedValues(areas),
      cityAreas: [...cityAreaMap.values()]
        .map(({ city, state, areas }) => ({
          city,
          state,
          areas: getUniqueSortedValues([...areas]),
        }))
        .sort((a, b) => a.city.localeCompare(b.city)),
    };
  }, [vendorsPayload?.vendors]);

  const cityOptions = useMemo(() => {
    if (backendLocationOptions?.cities?.length) {
      return getUniqueSortedValues(backendLocationOptions.cities);
    }

    if (fallbackLocationOptions.cities.length) {
      return fallbackLocationOptions.cities;
    }

    return regionDistricts || [];
  }, [
    backendLocationOptions?.cities,
    fallbackLocationOptions.cities,
    regionDistricts,
  ]);
  const cityAreaOptions = backendLocationOptions?.cityAreas?.length
    ? backendLocationOptions.cityAreas
    : fallbackLocationOptions.cityAreas;
  const allAreaOptions = backendLocationOptions?.areas?.length
    ? getUniqueSortedValues(backendLocationOptions.areas)
    : fallbackLocationOptions.areas;
  const areaSearchOptions = useMemo(() => {
    const optionMap = new Map();

    (cityAreaOptions || []).forEach((cityArea) => {
      (cityArea?.areas || []).forEach((area) => {
        const key = `${area}`.toLowerCase();
        if (!optionMap.has(key)) {
          optionMap.set(key, {
            area,
            city: cityArea?.city || "",
            state: cityArea?.state || "",
          });
        }
      });
    });

    allAreaOptions.forEach((area) => {
      const key = `${area}`.toLowerCase();
      if (!optionMap.has(key)) {
        optionMap.set(key, { area, city: "", state: "" });
      }
    });

    return [...optionMap.values()].sort((a, b) =>
      a.area.localeCompare(b.area),
    );
  }, [allAreaOptions, cityAreaOptions]);

  const citySearchOptions = useMemo(() => {
    const cityMap = new Map();

    cityOptions.forEach((city) => {
      const key = String(city).toLowerCase();
      const cityArea = cityAreaOptions.find(
        (item) => item?.city?.toLowerCase() === key,
      );

      if (!cityMap.has(key)) {
        cityMap.set(key, {
          city,
          state: cityArea?.state || "",
          areasCount: cityArea?.areas?.length || 0,
        });
      }
    });

    return [...cityMap.values()];
  }, [cityAreaOptions, cityOptions]);

  const selectedCityAreas = useMemo(() => {
    if (!selectedCity) return [];

    const cityArea = cityAreaOptions.find(
      (item) =>
        item?.city?.toLowerCase() === String(selectedCity).toLowerCase(),
    );

    return (cityArea?.areas || []).map((area) => ({
      area,
      city: cityArea?.city || selectedCity,
      state: cityArea?.state || "",
    }));
  }, [cityAreaOptions, selectedCity]);

  const locationSearchResults = useMemo(() => {
    const normalizedTerm = searchLocationTerm.trim().toLowerCase();
    const results = [];

    citySearchOptions.forEach((option) => {
      const haystack = [option.city, option.state].filter(Boolean).join(" ");
      const shouldShow = normalizedTerm
        ? haystack.toLowerCase().includes(normalizedTerm)
        : selectedCity
          ? option?.city?.toLowerCase() === String(selectedCity).toLowerCase()
          : true;

      if (shouldShow) results.push({ type: "city", ...option });
    });

    areaSearchOptions.forEach((option) => {
      const haystack = [option.area, option.city, option.state]
        .filter(Boolean)
        .join(" ");
      const shouldShow = normalizedTerm
        ? haystack.toLowerCase().includes(normalizedTerm)
        : selectedCity &&
          option?.city?.toLowerCase() === String(selectedCity).toLowerCase();

      if (shouldShow) results.push({ type: "area", ...option });
    });

    return results
      .sort((a, b) => {
        if (a.type !== b.type) return a.type === "city" ? -1 : 1;
        return (a.city || a.area).localeCompare(b.city || b.area);
      })
      .slice(0, normalizedTerm ? 14 : 10);
  }, [areaSearchOptions, citySearchOptions, searchLocationTerm, selectedCity]);

  const selectedAreaFilters = Array.isArray(appliedFilters.area)
    ? appliedFilters.area
    : appliedFilters.area
      ? [appliedFilters.area]
      : [];
  const selectedAreaTrails = selectedAreaFilters.map((area) => {
    const trail = areaSearchOptions.find(
      (option) => option?.area?.toLowerCase() === String(area).toLowerCase(),
    );

    return trail || { area, city: selectedCity, state: "" };
  });

  const applyFilterData = (filterPayload) => {
    dispatch(
      filterCompanyDirectory({
        filterData: filterPayload,
        searchKey,
        userId,
        entityId,
      }),
    );
  };

  const handleToggle = (key) => {
    setOpenKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleInputChange = (field, value, isMultiple) => {
    const filterPayload = updateCompanyDirectory({
      field,
      value,
      isMultiple,
      appliedFilters,
    });
    applyFilterData(filterPayload);
  };

  const handleRadioFilterChange = (field, value, allValue = "all") => {
    const filterPayload = { ...appliedFilters };

    if (value === allValue) {
      delete filterPayload[field];
    } else {
      filterPayload[field] = value;
    }

    applyFilterData(filterPayload);
  };

  const getGstPanAvailabilityValue = () => {
    if (appliedFilters?.gstPanAvailability) {
      return appliedFilters.gstPanAvailability;
    }

    const gstValue = appliedFilters?.gstNumberAvailability;
    const panValue = appliedFilters?.panNumberAvailability;

    if (gstValue === "with" && panValue === "with") return "with";
    if (gstValue === "without" && panValue === "without") return "without";

    return "all";
  };

  const handleGstPanAvailabilityChange = (value) => {
    const filterPayload = { ...appliedFilters };
    delete filterPayload.gstNumberAvailability;
    delete filterPayload.panNumberAvailability;

    if (value === "all") {
      delete filterPayload.gstPanAvailability;
    } else {
      filterPayload.gstPanAvailability = value;
    }

    applyFilterData(filterPayload);
  };

  const handleCityOptionChange = (city) => {
    const filterPayload = { ...appliedFilters };

    if (city === "all") {
      delete filterPayload.location;
      delete filterPayload.area;
      setSearchLocationTerm("");
    } else {
      filterPayload.location = city;
      delete filterPayload.area;
    }

    applyFilterData(filterPayload);
  };

  const handleAreaOptionChange = (areaOption) => {
    const areaValue = areaOption?.area || "";
    if (!areaValue) return;

    const isSelected = selectedAreaFilters.some(
      (area) => String(area).toLowerCase() === areaValue.toLowerCase(),
    );
    const selectedOptionCity = areaOption?.city || selectedCity;
    const shouldResetAreasForCity =
      selectedOptionCity &&
      selectedCity &&
      selectedOptionCity.toLowerCase() !== selectedCity.toLowerCase();
    const filterPayload = { ...appliedFilters };

    if (selectedOptionCity) filterPayload.location = selectedOptionCity;

    if (isSelected) {
      const nextAreas = selectedAreaFilters.filter(
        (area) => String(area).toLowerCase() !== areaValue.toLowerCase(),
      );
      if (nextAreas.length) {
        filterPayload.area = nextAreas;
      } else {
        delete filterPayload.area;
      }
    } else {
      filterPayload.area = shouldResetAreasForCity
        ? [areaValue]
        : [...selectedAreaFilters, areaValue];
    }

    applyFilterData(filterPayload);
  };

  const applyOrderValueFilter = (range) => {
    setCustomOrderValueError("");
    const filterPayload = { ...appliedFilters };

    if (!range) {
      delete filterPayload.totalOrderValue;
    } else {
      filterPayload.totalOrderValue = range;
    }

    applyFilterData(filterPayload);
  };

  const handleCustomOrderValueChange = (value, setter) => {
    const sanitizedValue = sanitizeAmountInput(value);

    setter(sanitizedValue);
    setCustomOrderValueError(
      value !== sanitizedValue ? "Only numbers are allowed." : "",
    );
  };

  const handleCustomOrderValueApply = () => {
    const min = parseAmountInput(customOrderValueMin);
    const max = parseAmountInput(customOrderValueMax);

    if (min === null && max === null) {
      setCustomOrderValueError("Enter min or max amount to apply custom range.");
      return;
    }

    if (min !== null && max !== null && min > max) {
      setCustomOrderValueError("Max amount should be greater than min amount.");
      return;
    }

    setCustomOrderValueError("");
    applyOrderValueFilter({
      label: `Rs. ${customOrderValueMin || "0"} - Rs. ${
        customOrderValueMax || "Above"
      }`,
      min: min ?? 0,
      max,
    });
  };

  const isOrderValueRangeSelected = (range) =>
    appliedFilters?.totalOrderValue?.label === range?.label;

  const getTagsByUserId = useCallback(async () => {
    if (!userId || !entityId) return;

    const response = await tagsService.fetchTagByUserId({ userId, entityId });
    const userTags = response.tags?.filter((tag) => tag !== "") || [];

    setTagsOptions(userTags);
    setTags(userTags);
  }, [entityId, userId]);

  const handleInputSearchTagsKey = (event) => {
    const value = event.target.value;
    setSearchTagTerm(value);
    setTagsOptions(
      tags?.filter((name) =>
        name?.toLowerCase()?.includes(value?.toLowerCase()),
      ),
    );
  };

  useEffect(() => {
    if (Array.isArray(backendCreatedByOptions)) {
      setAllCreatedByOptions(backendCreatedByOptions);
      setCreatedByOptions(backendCreatedByOptions);
      return;
    }

    if (appliedFilters?.addedBy?.length) return;

    const vendors = vendorsPayload?.vendors || [];
    const createdByCounts = vendors.reduce((counts, vendor) => {
      const createdBy = vendor?.addedBy;
      if (createdBy) {
        counts[createdBy] = (counts[createdBy] || 0) + 1;
      }
      return counts;
    }, {});
    const uniqueUsers = Object.keys(createdByCounts).map((value) => ({
      label: value,
      value,
      count: createdByCounts[value],
    }));

    if (uniqueUsers.length > 0) {
      setAllCreatedByOptions(uniqueUsers);
      setCreatedByOptions(uniqueUsers);
    }
  }, [
    appliedFilters?.addedBy,
    backendCreatedByOptions,
    vendorsPayload?.vendors,
  ]);

  useEffect(() => {
    if (!appliedFilters?.addedBy?.length && allCreatedByOptions?.length) {
      setCreatedByOptions(allCreatedByOptions);
    }
  }, [appliedFilters?.addedBy, allCreatedByOptions]);

  useEffect(() => {
    if (!isTagsFilterEnabled) return;

    dispatch(fetchTag());
    getTagsByUserId();
  }, [dispatch, getTagsByUserId, isTagsFilterEnabled]);

  useEffect(() => {
    if (!isTagsFilterEnabled) return;

    getTagsByUserId();
  }, [getTagsByUserId, isTagsFilterEnabled, vendorsPayload?.applyTagsCount]);

  useEffect(() => {
    dispatch(regionsActions());
  }, [dispatch]);

  useEffect(() => {
    async function loadTaxonomy() {
      const res = await axios.get(GET_VENDOR_TAXONOMY_URL);
      setTaxonomyAll(res.data.data);
    }
    loadTaxonomy();
  }, []);

  const renderFilterContent = ({ key, title, type, options }) => {
    if (type === "checkboxes") {
      return (
          <div className="px-2 py-1.5 text-xs">
          <CheckboxFilter
            addedFilters={appliedFilters}
            field={key}
            handleInputChange={handleInputChange}
            options={options}
            title={title}
          />
        </div>
      );
    }

    if (type === "radio" && key === "vendorActivity") {
      return (
        <FilterRadioRows
          counts={{
            all: vendorActivityCounts?.all ?? 0,
            "With Orders": vendorActivityCounts?.withOrders ?? 0,
            "Without Orders": vendorActivityCounts?.withoutOrders ?? 0,
          }}
          name="vendorActivity"
          onChange={(value) =>
            handleRadioFilterChange("vendorActivity", value)
          }
          options={[
            { label: "All vendors", value: "all" },
            { label: "With orders", value: "With Orders" },
            { label: "No orders", value: "Without Orders" },
          ]}
          selectedValue={appliedFilters?.vendorActivity || "all"}
        />
      );
    }

    if (type === "availability radio") {
      return (
        <FilterRadioRows
          name={key}
          onChange={(value) => handleRadioFilterChange(key, value)}
          options={options}
          selectedValue={appliedFilters?.[key] || "all"}
        />
      );
    }

    if (type === "gst pan availability radio") {
      return (
        <FilterRadioRows
          name={key}
          onChange={handleGstPanAvailabilityChange}
          options={options}
          selectedValue={getGstPanAvailabilityValue()}
        />
      );
    }

    if (type === "range") {
      return (
        <div className="px-3 py-2">
          <Slider
            className="mt-4"
            difference={10}
            endCount={100}
            onChange={handleInputChange}
            startCount={0}
            value={0}
          />
        </div>
      );
    }

    if (type === "search taxonomy") {
      return (
        <div className="px-3 py-2">
          <TaxonomyFilter
            appliedFilters={appliedFilters}
            handleInputChange={handleInputChange}
            taxonomyAll={taxonomyAll}
          />
        </div>
      );
    }

    if (type === "search checkbox") {
      if (key === "tags") {
        return (
          <div className="px-3 py-2">
            {tagsOptions?.length > 5 && (
              <input
                className="block w-full rounded-lg border border-[#DADAD0] bg-[#FCFCFA] px-2 py-2 text-xs text-gray-900 placeholder:text-xs focus:border-[#D65A22] focus:ring-[#D65A22]"
                onChange={handleInputSearchTagsKey}
                placeholder="Search tag name"
                type="text"
                value={searchTagTerm}
              />
            )}
            <CheckboxFilter
              addedFilters={appliedFilters}
              field={key}
              handleInputChange={handleInputChange}
              options={readMoreTags ? tagsOptions?.slice(0, 5) : tagsOptions}
              title={title}
            />
            {tagsOptions?.length > 5 && (
              <button
                className="mt-1 text-xs font-semibold text-[#0057D9]"
                onClick={() => setReadMoreTags((prev) => !prev)}
                type="button"
              >
                {readMoreTags ? "View More" : "View Less"}
              </button>
            )}
          </div>
        );
      }

      return (
        <div className="px-3 py-2">
          <CheckboxFilter
            addedFilters={appliedFilters}
            field={key}
            handleInputChange={handleInputChange}
            options={createdByOptions}
            title={title}
          />
        </div>
      );
    }

    if (type === "dateFilter") {
      return (
        <div>
          <CreatedDateFilter
            appliedFilters={appliedFilters}
            handleInputChange={handleInputChange}
          />
        </div>
      );
    }

    if (type === "dynamic financial year") {
      return (
        <FilterRadioRows
          name="financialYear"
          onChange={(value) => handleRadioFilterChange("financialYear", value)}
          options={[
            { label: "All years", value: "all" },
            ...financialYearOptions
              .slice()
              .reverse()
              .map((value) => ({
                label: formatFinancialYearLabel(value),
                value,
              })),
          ]}
          selectedValue={appliedFilters?.financialYear || "all"}
        />
      );
    }

    if (type === "radio" && key === "vendorInvitationCount") {
      return (
        <FilterRadioRows
          counts={{
            all: vendorInvitationCounts?.all ?? 0,
            invited: vendorInvitationCounts?.invited ?? 0,
            "0": vendorInvitationCounts?.nonInvited ?? 0,
          }}
          name="vendorInvitationCount"
          onChange={(value) =>
            handleRadioFilterChange("vendorInvitationCount", value)
          }
          options={[
            { label: "All Vendors", value: "all" },
            { label: "Invited Vendors", value: "invited" },
            { label: "Non-invited Vendors", value: "0" },
          ]}
          selectedValue={appliedFilters?.vendorInvitationCount || "all"}
        />
      );
    }

    if (type === "amount range") {
      return (
        <div className="space-y-1 px-3 py-2">
          <label className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-1 text-xs text-[#303026] hover:bg-[#FFF7F2]">
            <input
              checked={!appliedFilters?.totalOrderValue}
              className="h-4 w-4 border-gray-300 text-[#D65A22] focus:ring-[#D65A22]"
              name="totalOrderValue"
              onChange={() => applyOrderValueFilter(null)}
              type="radio"
            />
            <span>All Values</span>
          </label>
          {amountRangeOptions.map((range) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-1 text-xs text-[#303026] hover:bg-[#FFF7F2]"
              key={range.label}
            >
              <input
                checked={isOrderValueRangeSelected(range)}
                className="h-4 w-4 border-gray-300 text-[#D65A22] focus:ring-[#D65A22]"
                name="totalOrderValue"
                onChange={() => applyOrderValueFilter(range)}
                type="radio"
              />
              <span>{range.label}</span>
            </label>
          ))}
          <div className="my-2 border-t border-gray-200" />
          <div className="rounded-lg border border-[#E8E8E0] bg-[#FCFCFA] p-2">
            <p className="mb-2 text-xs font-semibold text-[#626252]">
              Custom Range
            </p>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-[11px] text-[#7A7A68]">
                Min Rs.
                <input
                  className="mt-1 block w-full rounded-md border border-[#DADAD0] bg-white px-2 py-1.5 text-xs text-gray-900 focus:border-[#D65A22] focus:ring-0 focus:ring-[#D65A22]"
                  inputMode="numeric"
                  onChange={(event) =>
                    handleCustomOrderValueChange(
                      event.target.value,
                      setCustomOrderValueMin,
                    )
                  }
                  pattern="[0-9]*"
                  type="text"
                  value={customOrderValueMin}
                />
              </label>
              <label className="text-[11px] text-[#7A7A68]">
                Max Rs.
                <input
                  className="mt-1 block w-full rounded-md border border-[#DADAD0] bg-white px-2 py-1.5 text-xs text-gray-900 focus:border-[#D65A22] focus:ring-0 focus:ring-[#D65A22]"
                  inputMode="numeric"
                  onChange={(event) =>
                    handleCustomOrderValueChange(
                      event.target.value,
                      setCustomOrderValueMax,
                    )
                  }
                  pattern="[0-9]*"
                  type="text"
                  value={customOrderValueMax}
                />
              </label>
            </div>
            {customOrderValueError && (
              <p className="mt-2 text-[11px] font-medium text-red-500">
                {customOrderValueError}
              </p>
            )}
            <button
              className="mt-2 w-full rounded-md border border-[#1E1E08] bg-white px-3 py-1.5 text-xs font-semibold text-[#1E1E08] hover:bg-[#F2F2EA]"
              onClick={handleCustomOrderValueApply}
              type="button"
            >
              Apply
            </button>
          </div>
        </div>
      );
    }

    if (type === "search radio") {
      return (
        <div className="space-y-2 px-3 py-2">
          <input
            className="block w-full rounded-lg border border-[#DADAD0] bg-[#FCFCFA] px-2 py-2 text-xs text-gray-900 placeholder:text-xs focus:border-[#D65A22] focus:ring-0 focus:ring-[#D65A22]"
            onChange={(event) => setSearchLocationTerm(event.target.value)}
            placeholder="Search city or area"
            type="text"
            value={searchLocationTerm}
          />

          {(selectedCity || selectedAreaTrails.length > 0) && (
            <div className="flex flex-wrap gap-1.5">
              {selectedCity && (
                <button
                  className="rounded-full border border-[#EBC7B6] bg-[#FFF7F2] px-2 py-[3px] text-left text-[10px] font-medium text-[#9A3412]"
                  onClick={() => handleCityOptionChange("all")}
                  type="button"
                >
                  City: {selectedCity} x
                </button>
              )}
              {selectedAreaTrails.map((trail) => (
                <button
                  className="rounded-full border border-[#EBC7B6] bg-[#FFF7F2] px-2 py-[3px] text-left text-[10px] font-medium text-[#9A3412]"
                  key={`${trail.city}-${trail.area}`}
                  onClick={() => handleAreaOptionChange(trail)}
                  type="button"
                >
                  {trail.area} x
                </button>
              ))}
            </div>
          )}

          <button
            className="flex w-full cursor-pointer items-center gap-2 rounded-md px-1 py-1 text-xs hover:bg-[#FFF7F2]"
            onClick={() => handleCityOptionChange("all")}
            type="button"
          >
            <input
              checked={!selectedCity}
              className="h-4 w-4 border-gray-300 text-[#D65A22] focus:ring-[#D65A22]"
              name="location"
              onChange={() => handleCityOptionChange("all")}
              onClick={(event) => event.stopPropagation()}
              type="radio"
            />
            <span>All locations</span>
          </button>

          {selectedCity && selectedCityAreas.length > 0 && !searchLocationTerm && (
            <p className="text-[10px] font-medium text-[#8A8A7A]">
              Showing areas in {selectedCity}
            </p>
          )}

          <div className="max-h-[240px] overflow-y-auto">
            {searchLocationTerm !== "" && locationSearchResults.length === 0 && (
              <p className="mt-2 text-center text-xs text-gray-400">
                No Match Found
              </p>
            )}

            {locationSearchResults.map((option) => {
              if (option.type === "city") {
                const checked =
                  selectedCity?.toLowerCase() === option.city?.toLowerCase();

                return (
                  <button
                    className={`flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg px-1 py-1.5 text-left hover:bg-[#FFF7F2] ${
                      checked ? "bg-[#FFF7F2]" : ""
                    }`}
                    key={`city-${option.city}`}
                    onClick={() => handleCityOptionChange(option.city)}
                    type="button"
                  >
                    <span className="flex min-w-0 items-start gap-2">
                      <input
                        checked={checked}
                        className="mt-0.5 h-4 w-4 shrink-0 border-gray-300 text-[#D65A22] focus:ring-[#D65A22]"
                        name="location"
                        onChange={() => handleCityOptionChange(option.city)}
                        onClick={(event) => event.stopPropagation()}
                        type="radio"
                      />
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate text-xs font-semibold text-[#303026]">
                          {option.city}
                        </span>
                        <LocationTrail
                          city={option.city}
                          state={option.state}
                        />
                      </span>
                    </span>
                    <span className="rounded-full bg-[#F2F2EA] px-2 py-[2px] text-[10px] text-[#626252]">
                      City
                    </span>
                  </button>
                );
              }

              const checked = selectedAreaFilters.some(
                (area) =>
                  String(area).toLowerCase() === option.area.toLowerCase(),
              );

              return (
                <button
                  className={`flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg px-1 py-1.5 text-left hover:bg-[#FFF7F2] ${
                    checked ? "bg-[#FFF7F2]" : ""
                  }`}
                  key={`area-${option.city}-${option.area}`}
                  onClick={() => handleAreaOptionChange(option)}
                  type="button"
                >
                  <span className="flex min-w-0 items-start gap-2">
                    <input
                      checked={checked}
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-[#D65A22] focus:ring-[#D65A22]"
                      onChange={() => handleAreaOptionChange(option)}
                      onClick={(event) => event.stopPropagation()}
                      type="checkbox"
                    />
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate text-xs font-semibold text-[#303026]">
                        {option.area}
                      </span>
                      <LocationTrail
                        area={option.area}
                        city={option.city}
                        state={option.state}
                      />
                    </span>
                  </span>
                  <span className="rounded-full bg-[#E9F7F2] px-2 py-[2px] text-[10px] text-[#007366]">
                    Area
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div>
      <div className="overflow-hidden bg-white">
        <FilterGroupTitle title="Vendor Type" />
        <ToggleFilterRow
          active={Boolean(appliedFilters.isPremium)}
          description="Subscribed to Neevay"
          icon={<PromotedIcon />}
          label="Promoted Profiles"
          onClick={() => handleInputChange("isPremium", true, false)}
        />
        <ToggleFilterRow
          active={Boolean(appliedFilters.isNeevayVendor)}
          description="Also in internal list"
          icon={<OnNeevayIcon />}
          label="On Neevay"
          onClick={() => handleInputChange("isNeevayVendor", true, false)}
        />
      </div>

      <div className="overflow-hidden bg-white">
        {visibleFilterFacets?.map((facet, index) => {
          const { key, title } = facet;
          const currentGroup = filterGroups[key] || "";
          const previousGroup =
            filterGroups[visibleFilterFacets[index - 1]?.key] || "";

          return (
            <React.Fragment key={key}>
              {currentGroup && currentGroup !== previousGroup && (
                <FilterGroupTitle title={currentGroup} />
              )}
              <div className="border-b border-[#E8E8E0]">
                <FilterHeader
                  onClick={() => handleToggle(key)}
                  open={openKeys[key]}
                  title={title}
                />
                {openKeys[key] && (
                  <div className="bg-white">{renderFilterContent(facet)}</div>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default AllFilters;
